/**
 * FlowRegistry
 * 
 * Stores and retrieves flow definitions.
 * Supports dynamic registration of flows.
 */

import { SquiggleFlow, Trigger, TriggerContext } from '../types';
import { ConditionEvaluator } from './ConditionEvaluator';

export class FlowRegistry {
  private flows: Map<string, SquiggleFlow> = new Map();

  /**
   * Register a flow
   */
  register(flow: SquiggleFlow): void {
    this.flows.set(flow.id, flow);
  }

  /**
   * Register multiple flows
   */
  registerAll(flows: SquiggleFlow[]): void {
    flows.forEach((flow) => this.register(flow));
  }

  /**
   * Get a flow by ID
   */
  get(id: string): SquiggleFlow | undefined {
    return this.flows.get(id);
  }

  /**
   * Get all flows
   */
  getAll(): SquiggleFlow[] {
    return Array.from(this.flows.values());
  }

  /**
   * Find flows matching a trigger context
   * Returns flows sorted by priority (highest first)
   */
  findByTrigger(
    context: TriggerContext,
    conditionEvaluator: ConditionEvaluator
  ): SquiggleFlow[] {
    const matchingFlows: SquiggleFlow[] = [];

    for (const flow of Array.from(this.flows.values())) {
      // Skip disabled flows
      if (flow.enabledByDefault === false) continue;

      // Check if any trigger matches
      const triggered = flow.triggers.some((trigger: Trigger) =>
        conditionEvaluator.evaluateTrigger(trigger, context)
      );

      if (triggered) {
        matchingFlows.push(flow);
      }
    }

    // Sort by priority (higher first)
    return matchingFlows.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }

  /**
   * Check if a flow exists
   */
  has(id: string): boolean {
    return this.flows.has(id);
  }

  /**
   * Unregister a flow
   */
  unregister(id: string): void {
    this.flows.delete(id);
  }

  /**
   * Clear all flows
   */
  clear(): void {
    this.flows.clear();
  }
}

export default FlowRegistry;
